/**
 * 벌점 현황 탭
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  PenaltySummary,
  PenaltyStatistics,
  PenaltyThreshold,
  THRESHOLD_COLORS,
} from '../../models/Penalty';

const PAGE_SIZE = 20;

interface PenaltySummaryTabProps {
  summaries: PenaltySummary[];
  statistics: PenaltyStatistics | null;
  thresholds: PenaltyThreshold[];
  total: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
}

export const PenaltySummaryTab: React.FC<PenaltySummaryTabProps> = ({
  summaries,
  statistics,
  thresholds,
  onRefresh,
}) => {
  // 임계값 색상 찾기
  const getThresholdColor = (thresholdId?: string): string => {
    if (!thresholdId) return 'transparent';
    const threshold = thresholds.find((t) => t.id === thresholdId);
    return threshold ? THRESHOLD_COLORS[threshold.color] || '#6b7280' : 'transparent';
  };

  const getThresholdName = (thresholdId?: string): string => {
    if (!thresholdId) return '정상';
    const threshold = thresholds.find((t) => t.id === thresholdId);
    return threshold?.name || '정상';
  };

  // 학생 검색 (이름) — 클라이언트 필터 + 클라이언트 페이지네이션
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summaries;
    return summaries.filter((s) => (s.student_name || '').toLowerCase().includes(q));
  }, [summaries, search]);

  // 검색어/데이터 변경 시 1페이지로
  useEffect(() => {
    setPage(1);
  }, [search, summaries]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const onPageChange = (p: number) => setPage(Math.min(Math.max(1, p), totalPages));
  const total = filtered.length;

  return (
    <div className="space-y-6">
      {/* 통계 카드 */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">전체 학생</div>
            <div className="text-2xl font-bold text-gray-900">{statistics.total_students}명</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">벌점 있는 학생</div>
            <div className="text-2xl font-bold text-red-600">
              {statistics.students_with_penalty}명
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">총 벌점</div>
            <div className="text-2xl font-bold text-gray-900">
              {statistics.total_penalty_points}점
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-500">평균 벌점</div>
            <div className="text-2xl font-bold text-gray-900">{statistics.average_points}점</div>
          </div>
        </div>
      )}

      {/* 임계값 범례 */}
      {thresholds.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">경고 레벨</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">정상</span>
            </div>
            {thresholds.map((threshold) => (
              <div key={threshold.id} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: THRESHOLD_COLORS[threshold.color] }}
                ></div>
                <span className="text-sm text-gray-600">
                  {threshold.name} ({threshold.points}점 이상)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 학생 목록 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
          <h3 className="font-medium text-gray-900 whitespace-nowrap">학생별 상벌점 현황</h3>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 학생 이름 검색"
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-48 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          <button
            onClick={onRefresh}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            새로고침
          </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg
              className="w-12 h-12 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p>{search.trim() ? '검색 결과가 없습니다.' : '벌점 현황이 없습니다.'}</p>
            <p className="text-sm mt-1">
              {search.trim() ? `"${search.trim()}" 와 일치하는 학생이 없습니다.` : '아직 벌점이 부과된 학생이 없습니다.'}
            </p>
          </div>
        ) : (
          <>
            {/* 🔴 overflow-x-auto 만으로는 부족하다. w-full 이면 표가 화면 폭에 눌려
                   '순벌점' 이 '순 / 벌 / 점' 으로 세로로 쌓인다. min-w-max 로 제 폭을
                   지키게 하고, 좁은 화면에서는 옆으로 밀어서 본다. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium whitespace-nowrap text-gray-500 uppercase">
                      학생
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-blue-500 uppercase">
                      상점
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-red-500 uppercase">
                      벌점
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-gray-500 uppercase">
                      순벌점
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-gray-500 uppercase">
                      경고 상태
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-gray-500 uppercase">
                      부과 횟수
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium whitespace-nowrap text-gray-500 uppercase">
                      기간 시작
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paged.map((summary) => (
                    <tr key={summary.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {summary.student_name || '이름 없음'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-blue-600 font-bold">
                          +{summary.merit_points || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-red-600 font-bold">
                          -{summary.demerit_points || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`text-lg font-bold ${
                            summary.current_points > 0 ? 'text-red-600' : 'text-gray-500'
                          }`}
                        >
                          {summary.current_points}점
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: summary.current_threshold_id
                              ? `${getThresholdColor(summary.current_threshold_id)}20`
                              : '#dcfce7',
                            color: summary.current_threshold_id
                              ? getThresholdColor(summary.current_threshold_id)
                              : '#16a34a',
                          }}
                        >
                          {getThresholdName(summary.current_threshold_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500">
                        {summary.penalty_count}회
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 text-sm">
                        {summary.period_start_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  총 {total}명 중 {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)}명
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    처음
                  </button>
                  <button
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - currentPage) <= 2)
                    .map(p => (
                      <button
                        key={p}
                        onClick={() => onPageChange(p)}
                        className={`px-3 py-1 text-sm rounded border ${
                          p === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  <button
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                  <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    마지막
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
